export const FREQUENCIA_LANCAMENTO_OPCOES = [
  { value: 'DIARIO', label: 'Diariamente' },
  { value: 'SEMANAL', label: 'Semanalmente' },
  { value: 'MENSAL', label: 'Mensalmente' },
  { value: 'ANUAL', label: 'Anualmente' },
]

/** Converte valor do formulário (pt-BR ou número) para float. */
export function parseValorBr(valor) {
  if (valor == null || valor === '') return 0
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0
  }
  const s = String(valor).trim()
  if (!s) return 0
  if (s.includes(',')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0
  }
  // "120.00" (toFixed / API) — ponto decimal, não milhar
  if (/^\d+\.\d{1,2}$/.test(s)) {
    return parseFloat(s) || 0
  }
  if (s.includes('.')) {
    return parseFloat(s.replace(/\./g, '')) || 0
  }
  return parseFloat(s) || 0
}

export function formatTotalBr(valor) {
  const n = parseValorBr(valor)
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Soma valores das linhas de pagamento (aba Pagamentos). */
export function somaPagamentosEmbarque(pagamentos) {
  return (pagamentos || []).reduce(
    (acc, p) => acc + parseValorBr(p?.valor),
    0
  )
}

/** Converte string YYYY-MM-DD ou Date para Date no fuso local (sem deslocar o dia). */
export function parseDataYmdLocal(valor) {
  if (valor == null || valor === '') return null
  if (valor instanceof Date) {
    return Number.isNaN(valor.getTime()) ? null : valor
  }
  const s = String(valor).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(valor)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Formata Date ou string para YYYY-MM-DD usando calendário local (sem UTC/toISOString). */
export function formatarDataYmdLocal(valor) {
  if (valor == null || valor === '') return null
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor.trim())) {
    return valor.trim()
  }
  const d = valor instanceof Date ? valor : parseDataYmdLocal(valor)
  if (!d || Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Normaliza competência para o 1º dia do mês (registro somente mês/ano). */
export function normalizarCompetenciaPrimeiroDia(valor) {
  const d = valor instanceof Date ? valor : parseDataYmdLocal(valor)
  if (!d || Number.isNaN(d.getTime())) return null
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** Formata competência para exibição MM/AAAA. */
export function formatarCompetenciaMesAno(valor) {
  const d = normalizarCompetenciaPrimeiroDia(valor)
  if (!d) return '--'
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${m}/${d.getFullYear()}`
}

/** Formata competência para API (YYYY-MM-01). */
export function formatarCompetenciaYmdLocal(valor) {
  const d = normalizarCompetenciaPrimeiroDia(valor)
  if (!d) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}-01`
}

/** Converte data do formulário/API para Date ou null. */
export function parseDataPagamentoFormulario(valor, fallback = new Date()) {
  if (valor == null || valor === '') {
    return fallback
  }
  const d = parseDataYmdLocal(valor)
  return d ?? fallback
}

/** Normaliza linhas de pagamento vindas da API para o estado do formulário. */
export function normalizarPagamentosEmbarqueDaApi(pagamentos, linhaVazia) {
  if (!Array.isArray(pagamentos) || pagamentos.length === 0) {
    return [linhaVazia()]
  }
  return pagamentos.map((pagamento) => ({
    ...pagamento,
    dataPagamento: parseDataPagamentoFormulario(
      pagamento?.dataPagamento ?? pagamento?.data_pagamento,
      new Date()
    ),
  }))
}

/** Formata data de pagamento para envio à API (YYYY-MM-DD). */
export function formatarDataPagamentoParaApi(valor) {
  return formatarDataYmdLocal(valor)
}

/**
 * Valor do lançamento: campo Valor (Geral) ou, se vazio, soma dos pagamentos.
 */
export function valorEfetivoCadastro(cadastro) {
  const valorInformado = parseValorBr(cadastro?.valor)
  if (valorInformado > 0) {
    return valorInformado
  }
  return somaPagamentosEmbarque(cadastro?.pagamentosEmbarque)
}

/** Valor do lançamento após desconto e acréscimo (mínimo 0). */
export function valorLiquidoCadastro(cadastro) {
  const bruto = valorEfetivoCadastro(cadastro)
  const desconto = parseValorBr(cadastro?.desconto)
  const acrescimo = parseValorBr(cadastro?.acrescimo)
  return Math.max(0, bruto - desconto + acrescimo)
}

/** Valor já pago (soma das linhas de pagamento ou campo legado valorPago). */
export function valorPagoCadastro(cadastro) {
  const pagamentos = cadastro?.pagamentosEmbarque || []
  if (pagamentos.length > 0) {
    return somaPagamentosEmbarque(pagamentos)
  }
  return parseValorBr(cadastro?.valorPago)
}

/** Saldo em aberto = valor líquido − valor pago (mesma regra do redux DESCONTO). */
export function saldoCadastro(cadastro) {
  return Math.max(0, valorLiquidoCadastro(cadastro) - valorPagoCadastro(cadastro))
}

/**
 * Total exibido no rodapé: saldo após pagamentos; se o redux calculou multa/juros
 * (cadastro.total), usa esse valor (comportamento anterior em contas a receber/pagar).
 */
export function totalExibicaoCadastro(cadastro) {
  const saldo = saldoCadastro(cadastro)
  const totalRedux = parseValorBr(cadastro?.total)
  const saldoRedux = parseValorBr(cadastro?.saldo)

  if (
    cadastro?.total != null &&
    cadastro?.total !== '' &&
    totalRedux > saldo + 0.009
  ) {
    return Math.max(0, totalRedux)
  }

  if (
    cadastro?.saldo != null &&
    cadastro?.saldo !== '' &&
    Math.abs(saldoRedux - saldo) < 0.02
  ) {
    return Math.max(0, saldoRedux)
  }

  return saldo
}

/** Indica se a soma dos pagamentos ultrapassa o valor líquido da conta. */
export function pagamentoExcedeValorConta(cadastro, pagamentosOverride = null) {
  const valor = valorLiquidoCadastro(cadastro)
  if (valor <= 0) return false
  const pagamentos = pagamentosOverride ?? cadastro?.pagamentosEmbarque
  const pago = somaPagamentosEmbarque(pagamentos)
  return pago > valor + 0.01
}

/** Status exibido no modal (PAGO quando pagamentos cobrem o valor líquido). */
export function statusCalculadoFromCadastro(cadastro) {
  const valor = valorLiquidoCadastro(cadastro)
  const pago = somaPagamentosEmbarque(cadastro?.pagamentosEmbarque)

  if (
    valor > 0 &&
    pago + 0.01 >= valor &&
    pago <= valor + 0.01
  ) {
    return 'PAGO'
  }

  const venc = cadastro?.vencimento
  if (venc) {
    const d = venc instanceof Date ? venc : new Date(venc)
    if (!Number.isNaN(d.getTime())) {
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)
      const vd = new Date(d)
      vd.setHours(0, 0, 0, 0)
      if (vd < hoje) {
        return 'VENCIDA'
      }
    }
  }

  return 'PENDENTE'
}

export function statusCadastroFinanceiro(raw) {
  const label = String(raw ?? 'PENDENTE').trim().toUpperCase() || 'PENDENTE'
  let classe = 'cadastro-financeiro-status-badge--pendente'
  if (label === 'PAGO') classe = 'cadastro-financeiro-status-badge--pago'
  else if (label === 'VENCIDA' || label === 'VENCIDO') {
    classe = 'cadastro-financeiro-status-badge--vencida'
  } else if (label !== 'PENDENTE') {
    classe = 'cadastro-financeiro-status-badge--outro'
  }
  return { label, classe }
}
